import { useState } from 'react';
import { BomCRUDSchema } from 'types/bom';
import { useDeleteBomMutation } from 'store/slices/apiV1/bom';
import { getCopy } from 'ui-component/DeleteConfirmationDialog/copy';
import { useDeleteProductionRunMutation } from 'store/slices/apiV1/production';
import { ProductionRun } from 'types/production';
import { ManagementActionTypes } from 'types';
import { handleErr } from 'utils/functions';
import {
  useDeleteSupplierMutation,
  useDeletePurchaseRuleMutation,
} from 'store/slices/apiV1/inventory';
import { useDeletePurchaseOrderMutation } from 'store/slices/apiV1/procurement';
import useSnackbar from 'hooks/useSnackbar';

interface useDeleteProps {
  id: BomCRUDSchema['id'] | ProductionRun['id'];
  actionType: Exclude<keyof typeof ManagementActionTypes, 'team'>;
  onSuccess?: () => void;
}

export default function useHandleDelete({
  id,
  actionType,
  onSuccess,
}: useDeleteProps) {
  const copy = getCopy();
  const { dispatchSuccessSnackbar } = useSnackbar();
  const [deleteBom, { isLoading: isDeletingBom, isSuccess: isSuccessBom }] =
    useDeleteBomMutation();
  const [
    deleteProductionRun,
    { isLoading: isDeletingProductionRun, isSuccess: isSuccessProductionRun },
  ] = useDeleteProductionRunMutation();

  const [
    deleteSupplier,
    { isLoading: isDeletingSupplier, isSuccess: isSuccessSupplier },
  ] = useDeleteSupplierMutation();

  const [
    deletePurchaseRule,
    { isLoading: isDeletingPurchaseRule, isSuccess: isSuccessPurchaseRule },
  ] = useDeletePurchaseRuleMutation();

  const [
    deletePurchaseOrder,
    { isLoading: isDeletingPurchaseOrder, isSuccess: isSuccessPurchaseOrder },
  ] = useDeletePurchaseOrderMutation();

  const [error, setError] = useState<string>('');

  const handleDelete = async () => {
    setError('');
    try {
      if (actionType === ManagementActionTypes.bom) {
        await deleteBom(id).unwrap();
      }
      if (actionType === ManagementActionTypes.productionRun) {
        await deleteProductionRun(id).unwrap();
      }
      if (actionType === ManagementActionTypes.supplier) {
        await deleteSupplier(id).unwrap();
      }
      if (actionType === ManagementActionTypes.purchaseRule) {
        await deletePurchaseRule(id).unwrap();
      }
      if (actionType === ManagementActionTypes.purchaseOrder) {
        await deletePurchaseOrder(id).unwrap();
      }
      dispatchSuccessSnackbar(copy[actionType].successMessage);
      onSuccess && onSuccess();
    } catch (err) {
      handleErr(err, setError);
    }
  };

  return {
    handleDelete,
    isLoading:
      isDeletingBom ||
      isDeletingProductionRun ||
      isDeletingSupplier ||
      isDeletingPurchaseRule ||
      isDeletingPurchaseOrder,
    isSuccess:
      isSuccessBom ||
      isSuccessProductionRun ||
      isSuccessSupplier ||
      isSuccessPurchaseRule,
    error,
    resetError: () => setError(''),
  };
}
